import { EventEmitter } from '@stencil/core';
import { store, type Unsubscribe } from '../store/stencil-redux';
import { BBox, Feature } from 'geojson';
import debounce from 'lodash.debounce';
import * as mapboxgl from 'mapbox-gl/esm';
import { AttributionControl, GeoJSONFeature, LngLatLike, Map as MapboxMap, ScaleControl } from 'mapbox-gl/esm';
import { AppConfig, CONFIGS } from '../config';
import { mapUpdateState } from '../store/map/map.actions';
import { MapInstanceState } from '../store/map/map.reducer';
import { resolvePersistedMapState } from '../store/map/resolve-persisted-map-state';
import { getAppBounds, getAppConfig, getAppConfigBingToken, getAppConfigMapsUrl, getAppId, getAppNetworkConnected, getAppSettings, getOfflineMapsMap } from '../store/selectors';
import { CoreoGeometry, CoreoMapFeature, CoreoMapFeatureType, CoreoProjectFeatures } from '../types';
import AppDatabase from './db/app-db.service';
import { eventsService } from './events.service';
import { geolocationStartTracking, state as geolocationState, onChange as onGeolocationChange, startDeviceOrientation } from './geolocation.service';
import { isBaseStyleLocked, mapboxMapStyles, mapboxMapStyleUrl, matchMapboxStyleId } from './geometry.service';
import { MapFeaturesService } from './map-features.service';
import { MapIconService } from './map-icon.service';
import { MapLayerManagerService } from './map-layer-manager.service';
import { MapboxBoundsLayerControl, MapboxUserLocationControl, emptyStyle, isMapStyleDark, pointToBox } from './mapbox.service';
import { MapLayer } from './maps/maps-base-layer';
import { runWhenMapIdle, safelyRemoveControl, waitForMapIdle } from './maps/maps.utils';
import { OfflineMapsService } from './offline-maps.service';
import { UKHabService } from './ukhab/ukhab.service';
import { StaticLayersRegistry } from './map-layer-static-registry.service';
import { DATA_LAYER_SOURCES } from '../configs/layers/ukhab-layers.config';
import { MapGeoJSONLayer } from './maps/maps-geojson-layer';
import { Capacitor } from '@capacitor/core';
import { BLUESKY_BOUNDS, BLUESKY_MIN_ZOOM, buildBlueSkyWmsStaticUrl, buildBlueSkyWmsTileTemplate, xyzTileToEpsg3857Bbox } from './bluesky-wms';

const PROBE_FETCH_TIMEOUT_MS = 3000;
const LOCATE_MIN_ZOOM = 14;

const fetchWithTimeout = async (url: string, init: RequestInit = {}, timeoutMs = PROBE_FETCH_TIMEOUT_MS): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

/** One z10 tile over London: a cheap GetMap through the same /wms path the map's tiles use. */
const BLUESKY_PROBE_TILE = { z: 10, x: 511, y: 340 };

const getFeatureType = (feature: GeoJSONFeature): CoreoMapFeatureType => {

  if (MapFeaturesService.instance.hasSource(feature.source)) {
    if (feature.properties != null && Object.prototype.hasOwnProperty.call(feature.properties, 'point_count')) {
      return 'cluster';
    } else {
      return 'record';
    }
  }
  const mapLayer = MapLayerManagerService.instance.getMapLayerByLayerId(feature.layer?.id);
  if (mapLayer?.state.layerType === 'custom') {
    return 'custom';
  }
  if (mapLayer?.state.layerType === 'static') {
    return 'data-layer';
  }
  if (mapLayer?.state.layerType === 'collection') {
    return 'item';
  };
  return 'custom';
}

const parseCoreoFeature = (feature: GeoJSONFeature): CoreoMapFeature => {
  const type = getFeatureType(feature);
  let sourceId: number;
  if (type === 'item') {
    sourceId = feature.properties.collectionId;
  } else if (type === 'record') {
    sourceId = feature.properties.surveyId;
  }

  return {
    type,
    sourceId,
    source: feature.source,
    geometry: feature.geometry as CoreoGeometry,
    properties: feature.properties,
    layerId: feature.layer?.id,
    id: feature.id as number
  };
}

export const mapInstanceDataLayersUpdated = (a: MapInstanceState, b: MapInstanceState): boolean => {
  const existingDataLayers = a.dataLayers.filter(l => l.enabled).map(l => l.sourceId);
  const currentDataLayers = b.dataLayers.filter(l => l.enabled).map(l => l.sourceId);
  return JSON.stringify(existingDataLayers) !== JSON.stringify(currentDataLayers);
}

export type MapFeatureFilter = () => (feature: Feature) => boolean;

export interface MapInterface {
  el: HTMLElement;
  previous: MapInterface;
  container: HTMLDivElement;

  bounds: any;
  boundsMaxZoom?: number;
  boundsPadding?: number;

  cluster: boolean;

  attributionPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

  tracking: boolean;
  showRecenter: boolean;

  state: MapInstanceState;

  mapReady: EventEmitter<mapboxgl.Map>;
  mapReleased: EventEmitter<void>;
  mapStyleLoad: EventEmitter<mapboxgl.Map>;
  featureClicked: EventEmitter<CoreoMapFeature[]>;
  mapClicked: EventEmitter<void>;
  featureFocused: EventEmitter<any>;
  mapStateChanged: EventEmitter<MapInstanceState>;
  mapStateUpdated: EventEmitter<MapInstanceState>;

  featureFilter?: MapFeatureFilter;

  background?: boolean;
  layers?: MapLayer[];
}

interface MapInformation {
  bounds: mapboxgl.LngLatBounds;
  center: mapboxgl.LngLat;
  bearing: number;
  pitch: number;
  zoom: number;
}

class MapService {

  private constructor() { }

  public static instance: MapService = new MapService();

  private map: mapboxgl.Map;
  private mapContainer: HTMLDivElement;
  private previousState: MapInstanceState;

  private activeMap: MapInterface;
  private mapInfo: WeakMap<MapInterface, MapInformation> = new WeakMap();
  private reconnectRafId: number | null = null;

  private intervalHandle: number;
  private networkSubscription: Unsubscribe;

  private locationHandle: MapboxUserLocationControl;
  private locationHasNonZeroHeading: boolean = false;
  private emptyHandle: number | undefined;
  private styleSwitchHandle: number | undefined;
  private static readonly STYLE_SWITCH_TIMEOUT_MS = 15000;
  private static readonly STYLE_METADATA_FAILURE_TIMEOUT_MS = 2000;
  private static readonly PROBE_CACHE_TTL_MS = 3000;
  private styleReachabilityCache = new Map<string, { result: boolean; expiresAt: number }>();
  private emptyGraceDeferred = false;
  private styleUpgradePending = false;
  private styleUpgradeDegraded = false;
  private styleLoadHandlerRunning = false;
  private attachedStyleKey: string | null = null;
  private styleChangeRevertId: string | null = null;
  private scaleControl: mapboxgl.ScaleControl;

  private appBoundsLayer: MapboxBoundsLayerControl;
  private networkConnected: boolean = false;

  private isNative: boolean = Capacitor.isNativePlatform();

  /**
   * Stencil only dispatches @Event() when the component host is connected. Skip emit otherwise
   * to avoid console warnings and dropped parent listeners.
   */
  private emitIfMapHostConnected(element: MapInterface | null | undefined, emit: () => void): void {
    if (!element?.el?.isConnected) {
      return;
    }
    emit();
  }

  /** Features can now change without a reload; leave a premium basemap (Bing, Bluesky) the plan no longer includes. */
  private handleProjectFeaturesUpdated = (projectId: number, features: CoreoProjectFeatures) => {
    const active = this.activeMap;
    if (!active || getAppId(store.getState()) !== projectId || !isBaseStyleLocked(features, active.state.baseStyleId)) {
      return;
    }
    active.state = { ...active.state, baseStyleId: mapboxMapStyles[0].id };
    this.emitIfMapHostConnected(active, () => active.mapStateUpdated.emit(active.state));
    store.getStore().dispatch(mapUpdateState(active.state));
    void this.setMapStyle(active.state.baseStyleId);
  };

  public init() {
    AppDatabase.instance.events.addListener('syncDidUpdate', debounce(async (projectId: number, records: boolean, config: boolean) => {
      const state = store.getState();
      if (!this.activeMap || getAppId(state) !== projectId) {
        return;
      }

      if (config) {
        await MapLayerManagerService.instance.refreshLayers();
      }

      if (records) {
        await this.loadProject(projectId, getAppConfig(state));
      }
    }, 300, { trailing: true }));
    AppDatabase.instance.events.addListener('projectFeaturesUpdated', this.handleProjectFeaturesUpdated);
    eventsService.addListener('appLoaded', this.onAppLoaded);
  }

  private onAppLoaded = async (appId: number) => {
    try {
      const state = store.getState();
      if (getAppId(state) !== appId || !this.activeMap) {
        return;
      }

      await MapLayerManagerService.instance.refreshLayers();
    } catch (error) {
      console.error('Error in onAppLoaded handler for appId:', appId, error);
    }
  }

  /**
 * Run a callback the next time the map is idle (no camera transition, no pending style/source work).
 * The map instance is passed as the first argument so callers don't need to call getMap().
 * Use when deferring style/layer mutations that could conflict with in-flight map updates (e.g. hiding
 * the cluster layer right after setData in a bulk apply). No-op if there is no map.
 */
  public runWhenIdle(fn: (map: mapboxgl.Map) => void): () => void {
    return runWhenMapIdle(this.map, fn);
  }

  /**
* Promise that resolves the next time the map is idle. Use when you need to await before running logic.
*/
  public whenIdle(): Promise<void> {
    return waitForMapIdle(this.map);
  }


  async loadProject(
    projectId: number,
    config: AppConfig,
    state?: MapInstanceState,
    featureFilter?: MapFeatureFilter
  ) {
    await MapIconService.instance.loadIcons(projectId, config);
    if (this.map && this.isNative) {
      await MapIconService.instance.applyAllIcons(this.map);
    }
    if (!this.activeMap) {
      return;
    }
    const mapFeatureFilter = featureFilter ?? this.activeMap.featureFilter;
    let mapState = state ?? this.activeMap.state;

    if (state != null && featureFilter != null) {
      const { resolved, shouldPersistToRedux } = resolvePersistedMapState(state, store.getState());
      mapState = resolved;
      this.activeMap.state = resolved;
      if (shouldPersistToRedux) {
        store.getStore().dispatch(mapUpdateState(resolved));
      }
    } else if (state != null) {
      this.activeMap.state = state;
      mapState = state;
    }

    await MapFeaturesService.instance.loadFeatures(mapState, mapFeatureFilter);
    if (state != null && featureFilter != null) {
      await this.applyMapState(this.activeMap, { skipDataLayerLoadFilter: true });
    }
  }

  private activeMapStateListener = async () => {
    const element = this.activeMap;
    if (!element) {
      return;
    }
    await this.applyMapState(element);
    this.emitIfMapHostConnected(element, () => element.mapStateUpdated.emit(element.state));
  };

  private getStyleAttachKey = (currentStyle: mapboxgl.StyleSpecification | null): string => {
    if (currentStyle?.name === 'empty') {
      return 'empty';
    }
    return this.activeMap?.state.baseStyleId ?? currentStyle?.name ?? '';
  };

  private pruneExpiredStyleReachabilityCache = () => {
    const now = Date.now();
    for (const [key, entry] of this.styleReachabilityCache) {
      if (entry.expiresAt <= now) {
        this.styleReachabilityCache.delete(key);
      }
    }
  };

  private clearStyleLoadRetryTimer = () => {
    if (typeof this.emptyHandle !== 'undefined') {
      window.clearTimeout(this.emptyHandle);
      this.emptyHandle = undefined;
    }
  };

  private clearStyleSwitchTimeout = () => {
    if (typeof this.styleSwitchHandle !== 'undefined') {
      window.clearTimeout(this.styleSwitchHandle);
      this.styleSwitchHandle = undefined;
    }
  };

  private scheduleStyleSwitchFailure = (delayMs = MapService.STYLE_SWITCH_TIMEOUT_MS) => {
    if (!this.styleUpgradePending) {
      return;
    }
    this.clearStyleSwitchTimeout();
    this.styleSwitchHandle = window.setTimeout(() => {
      this.styleSwitchHandle = undefined;
      this.handleStyleSwitchFailure();
    }, delayMs);
  };

  private isStyleMetadataFetchError = (event: mapboxgl.ErrorEvent): boolean => {
    const message = event.error?.message ?? '';
    if (message.includes('/styles/v1/') || message.includes('api.mapbox.com/styles')) {
      return true;
    }
    const errorWithUrl = event.error as Error & { url?: string };
    if (errorWithUrl.url?.includes('/styles/v1/') || errorWithUrl.url?.includes('api.mapbox.com/styles')) {
      return true;
    }
    return false;
  };

  private handleStyleSwitchFailure = () => {
    if (!this.styleUpgradePending) {
      return;
    }

    this.styleUpgradePending = false;
    this.clearStyleSwitchTimeout();

    if (this.styleChangeRevertId) {
      const revertId = this.styleChangeRevertId;
      this.styleChangeRevertId = null;
      const active = this.activeMap;
      if (active) {
        active.state = { ...active.state, baseStyleId: revertId };
        this.emitIfMapHostConnected(active, () => active.mapStateUpdated.emit(active.state));
        store.getStore().dispatch(mapUpdateState(active.state));
      }
      if (this.previousState) {
        this.previousState = { ...this.previousState, baseStyleId: revertId };
      }
      void this.setMapStyle(revertId);
      return;
    }

    this.styleUpgradeDegraded = true;

    let alreadyEmpty = false;
    try {
      alreadyEmpty = this.map.getStyle()?.name === 'empty';
    } catch {
      // still transitioning — fall through to reset
    }

    if (alreadyEmpty) {
      this.clearStyleLoadRetryTimer();
      if (this.attachedStyleKey !== 'empty') {
        void this.onMapStyleLoad();
      }
      return;
    }

    try {
      this.styleUpgradePending = true;
      this.map.setStyle(emptyStyle, {
        diff: false,
        localFontFamily: '',
        localIdeographFontFamily: ''
      });
      this.scheduleStyleSwitchFailure();
    } catch {
      this.styleUpgradePending = false;
      this.scheduleStyleLoadRetry();
    }
  };

  private scheduleStyleLoadRetry = (delayMs = 1000) => {
    if (this.attachedStyleKey != null && !this.styleUpgradePending && !this.styleChangeRevertId) {
      return;
    }
    this.clearStyleLoadRetryTimer();
    this.emptyHandle = window.setTimeout(() => {
      this.emptyHandle = undefined;
      void this.onMapStyleLoad();
    }, delayMs);
  };

  private onMapInitialLoad = () => {
    MapLayerManagerService.instance.setMap(this.map);

    // Check to see if we are initializeing to an offline map
    const offlineMap = getOfflineMapsMap(this.activeMap.state.baseStyleId)(store.getState());
    if (typeof offlineMap !== 'undefined') {
      void this.setMapStyle(this.activeMap.state.baseStyleId);
      return;
    }

    // Try to upgrade every 5 seconds
    this.intervalHandle = window.setInterval(this.upgradeMap, 5000);

    // Also try to upgrade as soon as the network becomes online
    this.networkConnected = getAppNetworkConnected(store.getState());
    this.networkSubscription = store.getStore().subscribe(() => {
      const networkConnected = getAppNetworkConnected(store.getState());
      const reconnected = networkConnected && !this.networkConnected;
      this.networkConnected = networkConnected;
      if (reconnected) {
        this.styleUpgradeDegraded = false;
        this.upgradeMap();
      }
    });

    // Try to upgrade immediately
    if (this.networkConnected) {
      this.upgradeMap();
    }
  };

  private onTouchmove = () => {
    if (this.activeMap) {
      this.activeMap.tracking = false;
    }
  }

  private onRotateEnd = () => {
    if (this.activeMap) {
      this.activeMap.showRecenter = this.map.getBearing() !== 0;
    }
  }

  private onMapLoad = () => {
    this.map.on('touchmove', this.onTouchmove);
    this.map.on('rotateend', this.onRotateEnd);
    this.locationHandle = new MapboxUserLocationControl(geolocationState.location);
    this.map.addControl(this.locationHandle);

    if (geolocationState.heading !== null) {
      this.updateHeading(geolocationState.heading);
    }
  };

  private onMapStyleLoad = async () => {
    if (!this.map || !this.activeMap) {
      return;
    }

    if (this.styleLoadHandlerRunning) {
      this.scheduleStyleLoadRetry(100);
      return;
    }

    this.styleLoadHandlerRunning = true;

    try {
      let currentStyle: mapboxgl.StyleSpecification | null;
      try {
        currentStyle = this.map.getStyle() ?? null;
      } catch {
        if (this.attachedStyleKey != null && !this.styleUpgradePending && !this.styleChangeRevertId) {
          return;
        }
        // Style transition in progress — wait until the style is stable
        this.scheduleStyleLoadRetry();
        return;
      }

      // Style is queryable — any in-flight setStyle transition has finished
      this.styleUpgradePending = false;
      this.clearStyleSwitchTimeout();

      const styleKey = this.getStyleAttachKey(currentStyle);
      if (styleKey === this.attachedStyleKey) {
        this.clearStyleLoadRetryTimer();
        return;
      }

      if (currentStyle?.name === 'empty') {
        if (!this.emptyGraceDeferred) {
          this.emptyGraceDeferred = true;
          this.scheduleStyleLoadRetry(3000);
          return;
        }
        // Grace period expired — attach layers on stable empty basemap
      } else {
        this.cancelUpgradeMap();
        this.emptyGraceDeferred = false;
        this.styleUpgradeDegraded = false;
      }

      this.clearStyleLoadRetryTimer();

      try {
        // Attach the features layers
        await MapFeaturesService.instance.addTo(this.map);

        // Add static layer sources
        StaticLayersRegistry.instance.addTo(this.map);

        await MapFeaturesService.instance.filterMapFeatures(this.activeMap.state);
        MapFeaturesService.instance.setDarkBasemap(currentStyle ? isMapStyleDark(currentStyle) : false);

        // Update clustering
        MapFeaturesService.instance.updateClustering(this.activeMap.cluster);

        // Render the maps state
        await this.applyMapState(this.activeMap);
        this.attachedStyleKey = styleKey;
        this.styleChangeRevertId = null;
        this.clearStyleLoadRetryTimer();
        const mapRef = this.map;
        const active = this.activeMap;
        if (mapRef && active) {
          this.emitIfMapHostConnected(active, () => active.mapStyleLoad.emit(mapRef));
        }
      } catch (error) {
        this.attachedStyleKey = null;
        console.warn('Failed to attach map layers after style load', error);
        this.scheduleStyleLoadRetry();
      }
    } finally {
      this.styleLoadHandlerRunning = false;
    }
  };

  private onMapError = (event: mapboxgl.ErrorEvent) => {
    if (!this.styleUpgradePending) {
      return;
    }

    if ('tile' in event && event.tile) {
      return;
    }

    if (this.isStyleMetadataFetchError(event)) {
      this.handleStyleSwitchFailure();
      return;
    }

    this.scheduleStyleSwitchFailure(MapService.STYLE_METADATA_FAILURE_TIMEOUT_MS);
  };

  private imageMissingHandler = async (e: { id: string }) => {
    if (e.id === 'ukhab-tree') {
      return MapIconService.instance.applyIcon(this.map, e.id, '/assets/imgs/ukhab-tree.png');
    }

    if (e.id.startsWith('#')) {
      MapIconService.instance.handleColor(this.map, e.id);
      return;
    }

    // Try UKHab
    if (UKHabService.instance.handleUKHab(this.map, e.id) !== null) {
      return;
    }

    if (await MapIconService.instance.handleIcon(this.map, e.id) !== null) {
      return;
    }


  }

  private onMapClick = async (e: mapboxgl.MapMouseEvent) => {
    const clickedFeatures: any[] = this.map.queryRenderedFeatures(pointToBox(e.point, 15), {
      validate: false
    });

    if (clickedFeatures.some(s => s.layer?.id?.startsWith('MAP_GEOMETRY_SELECTOR'))) {
      return;
    }


    const features = clickedFeatures
      // Exclude highlight layers
      .filter(f => !f.layer?.id?.endsWith('-highlight'))
      // Exclude preview features (ie from split)
      .filter(f => f.properties?.__isPreview !== true)
      .filter(f => {
        return f.source.startsWith('coreo') || DATA_LAYER_SOURCES.has(f.source);
      })
      // Remove duplicates
      .filter((value, index, arr) => index === arr.findIndex(t => t.id === value.id))
      .map(f => parseCoreoFeature(f));

    for (const feature of features) {
      if (feature.type === 'custom') {
        const mapLayerState = MapLayerManagerService.instance.getMapLayerByLayerId(feature.layerId);
        if (mapLayerState?.layer instanceof MapGeoJSONLayer) {
          const geoJSONFeature = await mapLayerState.layer.getFeature(feature.id);
          if (geoJSONFeature && geoJSONFeature.geometry) {
            feature.geometry = geoJSONFeature.geometry as CoreoGeometry;
          }
        }
      }
    }

    if (features.length === 0) {
      this.fireMapClickEvent(e);
    }
    const active = this.activeMap;
    this.emitIfMapHostConnected(active, () => active.featureClicked.emit(features));
  };

  public fireMapClickEvent(e?: mapboxgl.MapMouseEvent) {
    this.map?.fire('coreo.mapClicked', { originalEvent: e });
    const active = this.activeMap;
    this.emitIfMapHostConnected(active, () => active.mapClicked.emit());
  }

  private async resolveMapStyle(styleId: string): Promise<mapboxgl.StyleSpecification | string> {
    // Check bing
    if (styleId === 'bing') {
      return this.loadBingMapStyle();
    }

    // Check bluesky
    if (styleId === 'bluesky') {
      return this.loadBlueSkyMapStyle();
    }

    // Check mapbox styles
    const mapboxStyle = matchMapboxStyleId(styleId);
    if (mapboxStyle) {
      return mapboxMapStyleUrl(mapboxStyle);
    }

    // Check offline maps
    const offlineMap = getOfflineMapsMap(styleId)(store.getState());
    if (offlineMap) {
      return OfflineMapsService.instance.getOfflineMap(offlineMap);
    }

    return emptyStyle;
  }

  private async loadBingMapStyle(): Promise<mapboxgl.StyleSpecification> {
    const imagerySet = 'Aerial';
    const token = getAppConfigBingToken(store.getState());
    const culture = 'en-GB';
    const d = await fetch(`https://dev.virtualearth.net/REST/V1/Imagery/Metadata/${imagerySet}?output=json&uriScheme=https&include=ImageryProviders&key=${token}`);
    const data = await d.json();
    const resourceSets = data.resourceSets[0];
    const resources = resourceSets.resources;
    const resource = resources[0];

    const imageUrl: string = resource.imageUrl;
    const imageUrlSubdomains: string[] = resource.imageUrlSubdomains;

    const tiles = imageUrlSubdomains.map(subdomain => {
      return imageUrl.replace('{subdomain}', subdomain)
        .replace('{culture}', culture);
    });

    const minzoom = resource.zoomMin;
    // 20 seems to be the practical max zoom level, so don't go below that
    const maxzoom = Math.min(resource.zoomMax, 20);
    const attribution = [
      `<a target="_blank" href="https://www.bing.com/maps" data-bm="101">© 2025 Microsoft</a>`,
      `&copy; TomTom`,
      `<a target="_blank" href="https://www.openstreetmap.org/copyright" data-bm="101">© OpenStreetMap</a>`,
      `Earthstar Geographics  SIO`
    ].join(', ');

    return {
      version: 8,
      glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
      name: 'Bing Aerial',
      sources: {
        'bing': {
          type: 'raster',
          tiles: tiles,
          tileSize: 256,
          minzoom,
          maxzoom,
          data: {
            attribution
          }
        }
      },
      layers: [{
        id: 'bing',
        type: 'raster',
        source: 'bing'
      }]
    };
  }

  private loadBlueSkyMapStyle(): mapboxgl.StyleSpecification {
    // Tiles go through the maps Bluesky proxy (server-held uuid). Upstream only
    // publishes a UK-bounded WebMercatorGB tile matrix, which is not Mapbox
    // XYZ-compatible, so raster tiles use WMS GetMap with {bbox-epsg-3857}.
    const tiles = [buildBlueSkyWmsTileTemplate(getAppConfigMapsUrl(store.getState()))];
    const attribution = '© Bluesky International';

    return {
      version: 8,
      glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
      // The published coreo-mapbox (0.4.1) picks geometry colours by matching this name against 'BlueSky';
      // keep the old casing until 0.4.2 (case-insensitive) is published and installed, then rename.
      name: 'BlueSky Aerial',
      sources: {
        'bluesky': {
          type: 'raster',
          tiles,
          tileSize: 256,
          bounds: BLUESKY_BOUNDS,
          minzoom: BLUESKY_MIN_ZOOM,
          maxzoom: 20,
          attribution
        }
      },
      layers: [{
        id: 'bluesky',
        type: 'raster',
        source: 'bluesky'
      }]
    };
  }

  public isBaseStyleReachable(styleId: string): Promise<boolean> {
    return this.probeStyleReachable(styleId);
  }

  private async probeStyleReachable(styleId: string): Promise<boolean> {
    const networkConnected = getAppNetworkConnected(store.getState());

    if (networkConnected) {
      const cached = this.styleReachabilityCache.get(styleId);
      if (cached) {
        if (cached.expiresAt > Date.now()) {
          return cached.result;
        }
        this.styleReachabilityCache.delete(styleId);
      }
    } else {
      this.styleReachabilityCache.delete(styleId);
    }

    let result: boolean;

    if (styleId === 'bing') {
      if (!networkConnected) {
        result = false;
      } else {
        try {
          const token = getAppConfigBingToken(store.getState());
          const response = await fetchWithTimeout(
            `https://dev.virtualearth.net/REST/V1/Imagery/Metadata/Aerial?output=json&uriScheme=https&key=${encodeURIComponent(token)}`,
            { cache: 'no-store' }
          );
          result = response.ok;
        } catch {
          result = false;
        }
      }
    } else if (styleId === 'bluesky') {
      if (!networkConnected) {
        result = false;
      } else {
        try {
          const { z, x, y } = BLUESKY_PROBE_TILE;
          const url = buildBlueSkyWmsStaticUrl(getAppConfigMapsUrl(store.getState()), xyzTileToEpsg3857Bbox(z, x, y));
          const response = await fetchWithTimeout(url, { cache: 'no-store' });
          result = response.ok && (response.headers.get('content-type') ?? '').startsWith('image/');
          // Only the status matters; don't download the tile.
          void response.body?.cancel().catch(() => undefined);
        } catch {
          result = false;
        }
      }
    } else {
      const mapboxStyle = matchMapboxStyleId(styleId);
      if (mapboxStyle) {
        if (!networkConnected) {
          result = false;
        } else {
          const token = CONFIGS.prod.MAPBOX_TOKEN;
          if (!token) {
            result = false;
          } else {
            try {
              const response = await fetchWithTimeout(
                `https://api.mapbox.com/styles/v1/mapbox/${mapboxStyle}?access_token=${encodeURIComponent(token)}`,
                { cache: 'no-store' }
              );
              result = response.ok;
            } catch {
              result = false;
            }
          }
        }
      } else {
        const offlineMap = getOfflineMapsMap(styleId)(store.getState());
        if (offlineMap) {
          result = true;
        } else {
          result = false;
        }
      }
    }

    if (networkConnected) {
      this.pruneExpiredStyleReachabilityCache();
      this.styleReachabilityCache.set(styleId, {
        result,
        expiresAt: Date.now() + MapService.PROBE_CACHE_TTL_MS
      });
    }

    return result;
  }

  private async setMapStyle(styleId: string): Promise<void> {
    this.attachedStyleKey = null;
    try {
      const style = await this.resolveMapStyle(styleId);
      this.styleUpgradeDegraded = false;
      this.clearStyleSwitchTimeout();
      this.styleUpgradePending = true;
      this.map.setStyle(style, {
        diff: false,
        localFontFamily: '',
        localIdeographFontFamily: ''
      });
      this.scheduleStyleSwitchFailure();
    } catch (error) {
      this.styleUpgradePending = false;
      this.clearStyleSwitchTimeout();
      this.styleUpgradeDegraded = true;
      if (this.styleChangeRevertId && this.activeMap) {
        const revertId = this.styleChangeRevertId;
        this.styleChangeRevertId = null;
        this.activeMap.state = { ...this.activeMap.state, baseStyleId: revertId };
        this.emitIfMapHostConnected(this.activeMap, () => this.activeMap.mapStateUpdated.emit(this.activeMap.state));
        store.getStore().dispatch(mapUpdateState(this.activeMap.state));
        if (this.previousState) {
          this.previousState = { ...this.previousState, baseStyleId: revertId };
        }
      }
      console.warn('Failed to resolve or set map style', error);
    }
  }

  private upgradeMap = async () => {
    if (!this.networkConnected || this.styleUpgradePending) {
      return;
    }

    if (this.styleUpgradeDegraded && this.attachedStyleKey === 'empty') {
      const reachable = await this.probeStyleReachable(this.activeMap.state.baseStyleId);
      if (!reachable) {
        return;
      }
      this.styleUpgradeDegraded = false;
    }

    try {
      const currentStyle = this.map?.getStyle();
      if (!currentStyle) {
        return;
      }

      if (currentStyle.name === 'empty') {
        await this.setMapStyle(this.activeMap.state.baseStyleId);
      } else {
        this.cancelUpgradeMap();
      }
    } catch {
      // Style transition in progress
    }
  }

  private cancelUpgradeMap() {
    if (typeof this.intervalHandle !== 'undefined') {
      window.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    if (typeof this.networkSubscription !== 'undefined') {
      this.networkSubscription();
      this.networkSubscription = undefined;
    }
  }

  private initializeMap(request: MapInterface) {
    const { container, bounds, boundsMaxZoom, boundsPadding } = request;

    // Create a container for our map
    this.mapContainer = document.createElement('div');
    this.mapContainer.classList.add('map-master');

    // Append it into our first requester
    container.prepend(this.mapContainer);

    const options: mapboxgl.MapOptions = {
      accessToken: CONFIGS.prod.MAPBOX_TOKEN,
      container: this.mapContainer,
      style: emptyStyle,
      bounds,
      fitBoundsOptions: {
        maxZoom: boundsMaxZoom ?? 12,
        padding: boundsPadding ?? 40
      },
      attributionControl: false,
      trackResize: true,
      projection: 'mercator' as any,
      transformRequest: OfflineMapsService.instance.offlineMapTransformRequestFunction,
      fadeDuration: 0,
      // Memory Optimisations
      antialias: false,
      preserveDrawingBuffer: false,
      maxPitch: 60,
      renderWorldCopies: false,
      performanceMetricsCollection: false
    };
    this.map = new MapboxMap(options);
    this.map.addControl(new AttributionControl(), request.attributionPosition ?? 'bottom-right');

    // Setup upgrade handles
    this.map.once('load', this.onMapInitialLoad);
    this.map.on('load', this.onMapLoad);
    this.map.on('style.load', this.onMapStyleLoad);
    this.map.on('error', this.onMapError);
    this.map.on('styleimagemissing', this.imageMissingHandler);

    // Setup generic coreo map feature click handler
    this.map.on('click', this.onMapClick);

    geolocationStartTracking().then(() => {
      startDeviceOrientation();
    });

    onGeolocationChange('location', location => {
      if (this.locationHandle) {
        this.locationHandle.setPosition(location);
      }
      if (this.activeMap?.tracking) {
        const { retainZoomOnLocate } = getAppSettings(store.getState());
        this.map.easeTo({
          center: location as LngLatLike,
          ...(retainZoomOnLocate ? {} : { zoom: Math.max(this.map.getZoom(), LOCATE_MIN_ZOOM) }),
          duration: 500
        });
      }
    });
    onGeolocationChange('heading', this.updateHeading);

    if (this.isNative) {
      MapIconService.instance.applyAllIcons(this.map);
    }

    return this.map;
  }

  private updateHeading = (heading: number) => {
    // Only trust the compass if we have ever received a non-cardinal direction.
    // This filters out devices (some Androids) that fake a compass with fixed 0/90/180/270 values.
    this.locationHasNonZeroHeading = this.locationHasNonZeroHeading || ((heading % 90) !== 0);

    if (this.locationHandle && this.locationHasNonZeroHeading) {
      this.locationHandle.setHeading(heading);
    }
  };

  private storeMapInfo(element: MapInterface) {
    this.mapInfo.set(element, {
      bounds: this.map.getBounds(),
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch()
    });
  }

  private mapRequestQueue: Promise<mapboxgl.Map> | null = null;
  private pendingRequestElement: MapInterface | null = null;

  public async requestMap(element: MapInterface): Promise<mapboxgl.Map> {
    // If this is element is already being processed, return the existing promise
    if (this.pendingRequestElement === element && this.mapRequestQueue) {
      return this.mapRequestQueue;
    }

    // If there's a request in progress for a different element, wait for it
    if (this.mapRequestQueue && this.pendingRequestElement !== element) {
      await this.mapRequestQueue;
    }

    this.pendingRequestElement = element;
    this.mapRequestQueue = this.requestMapInternal(element);

    try {
      const result = await this.mapRequestQueue;
      return result;
    } finally {
      if (this.pendingRequestElement === element) {
        this.pendingRequestElement = null;
        this.mapRequestQueue = null;
      }
    }

  }

  private async requestMapInternal(element: MapInterface): Promise<mapboxgl.Map> {
    // If we have an existing map, store it's information first
    if (this.activeMap && this.activeMap !== element) {
      this.storeMapInfo(this.activeMap);
      this.activeMap.el.removeEventListener('mapStateChanged', this.activeMapStateListener);
      this.hideMap(this.activeMap.container);
      (this.activeMap.layers || []).map(l => l.remove());
      const previousActive = this.activeMap;
      this.emitIfMapHostConnected(previousActive, () => previousActive.mapReleased.emit());
    }

    // Set the currently active maps previous pointer to the incoming map
    element.previous = this.activeMap;

    // Set our new active map
    this.activeMap = element;
    this.activeMap.el.addEventListener('mapStateChanged', this.activeMapStateListener, {
      passive: true
    });

    if (!this.map) {
      return this.initializeMap(element);
    }

    return new Promise<mapboxgl.Map>((resolve) => {
      // Move the map now it's hidden
      const attachMap = async () => {

        if (this.activeMap !== element) {
          return resolve(this.map);
        }

        // Hide the container element whilst we move the map into it
        this.hideMap(element.container);

        // Move the map into the container
        element.container.appendChild(this.mapContainer);

        // Resize the map in its new location
        this.map.resize();

        // Update the center of the map
        const { bounds, boundsPadding, boundsMaxZoom } = element;

        if (bounds) {
          this.map.fitBounds(bounds, {
            padding: boundsPadding,
            maxZoom: boundsMaxZoom,
            duration: 0,
            maxDuration: 0
          });
        }

        // Update clustering
        MapFeaturesService.instance.updateClustering(element.cluster);

        await MapIconService.instance.applyAllIcons(this.map);

        // Apply the state
        const applied = await this.applyMapState(element);
        if (applied && this.activeMap === element) {
          this.emitIfMapHostConnected(element, () => {
            element.mapReady.emit(this.map);
            element.mapStyleLoad.emit(this.map);
          });
        }

        // Reveal the map
        this.revealMap(element.container).then(() => {
          resolve(this.map);
        });
      };
      requestAnimationFrame(attachMap);
    });
    // return this.map;
  }

  private revealMap(container: HTMLDivElement): Promise<void> {
    let done = false;
    let safetyTimeoutId: number | undefined;

    return new Promise<void>((resolve) => {
      const removeHidden = () => {
        if (done) {
          return;
        }
        done = true;
        if (typeof safetyTimeoutId !== 'undefined') {
          window.clearTimeout(safetyTimeoutId);
          safetyTimeoutId = undefined;
        }
        container?.classList.remove('map-hidden');
        this.map.off('render', removeHidden);
        resolve();
      };
      this.map.once('render', removeHidden);
      safetyTimeoutId = window.setTimeout(removeHidden, 500);
    });
  }

  private hideMap(container: HTMLDivElement) {
    container.classList.add('map-hidden');
  }

  private reconnectMap = async () => {
    const element = this.activeMap;
    // Move the map to its new location and resize
    element.container.appendChild(this.mapContainer);

    if (element.background) {
      return;
    }
    this.map.resize();

    // Center the map back to its previous location
    const info = this.mapInfo.get(element);
    if (!info) {
      return;
    }
    const { bounds, bearing, pitch } = info;
    const { center, zoom } = this.map.cameraForBounds(bounds, {
      padding: 0
    });

    this.map.jumpTo({
      center,
      zoom,
      bearing,
      pitch
    });

    // Attach listener
    element.el.addEventListener('mapStateChanged', this.activeMapStateListener, {
      passive: true
    });

    // Apply state
    MapFeaturesService.instance.updateClustering(element.cluster);
    element.showRecenter = bearing !== 0;
    await this.applyMapState(element);

    // Re-trigger the map events
    if (this.activeMap === element) {
      this.emitIfMapHostConnected(element, () => {
        element.mapReady.emit(this.map);
        element.mapStyleLoad.emit(this.map);
      });
    }

    // Finally remove the hidden
    this.revealMap(element.container);

    // Consume stored state so the next "background → foreground" without another map
    // taking the map does not trigger a full reconnect (see notifyBackgroundChange).
    if (this.activeMap === element) {
      this.mapInfo.delete(element);
    }
  }

  /** Schedules at most one reconnect on the next frame; coalesces callers (e.g. releaseMap + notifyBackgroundChange). Only runs if activeMap is still the same and still has mapInfo. */
  private scheduleReconnect = () => {
    if (this.reconnectRafId !== null) {
      return;
    }
    const target = this.activeMap;
    if (!target) {
      return;
    }
    this.reconnectRafId = requestAnimationFrame(() => {
      this.reconnectRafId = null;
      if (this.activeMap !== target || !this.mapInfo.has(target)) {
        return;
      }
      this.reconnectMap();
    });
  };

  public async releaseMap(element: MapInterface) {
    this.activeMap = element.previous;
    element.el.removeEventListener('mapStateChanged', this.activeMapStateListener);
    element.previous = undefined;
    element.container = undefined;
    (element.layers || []).map(layer => layer.remove());
    element.mapReleased.emit();

    if (!this.activeMap) {
      return;
    }

    // Persist the base style
    this.activeMap.state.baseStyleId = element.state.baseStyleId;
    this.scheduleReconnect();
  }

  public getBounds(): BBox {
    return this.map?.getBounds().toArray().flat() as BBox;
  }

  public getMap() {
    return this.map;
  }

  private updateScaleBar(showScaleBar: boolean) {
    // If we have the control and we don't want it, remoe it
    const hasControl = this.map.hasControl(this.scaleControl);
    if (!showScaleBar && hasControl) {
      this.map.removeControl(this.scaleControl);
    } else if (showScaleBar) {
      if (typeof this.scaleControl === 'undefined') {
        this.scaleControl = new ScaleControl({
          unit: 'metric',
          maxWidth: 240
        });
      }
      if (!hasControl) {
        this.map.addControl(this.scaleControl, 'top-left');
      }
    }
  }

  private updateProjectBounds(showProjectBounds: boolean) {
    const appBounds = getAppBounds(store.getState());
    // If we are showing bounds, and we don't already have one
    if (this.appBoundsLayer) {
      safelyRemoveControl(this.map, this.appBoundsLayer);
    }

    if (showProjectBounds && appBounds) {
      this.appBoundsLayer = new MapboxBoundsLayerControl(appBounds);
      this.map.addControl(this.appBoundsLayer);
    } else if (this.appBoundsLayer && (!showProjectBounds || !appBounds)) {
      this.appBoundsLayer = undefined;
    }
  }

  /**
   * Re-apply feature filtering to the currently active map.
   * When featureFilter is provided (e.g. from records after filter change), use it with activeMap.state.
   * When not provided, use activeMap.state and activeMap.featureFilter.
   * Caller is responsible for only calling when the intended map is active (e.g. records only calls when on records page).
   */
  public async triggerFeaturesRefresh(featureFilter?: MapFeatureFilter): Promise<void> {
    if (!this.activeMap) {
      return;
    }
    const filter = featureFilter ?? this.activeMap.featureFilter;
    await MapFeaturesService.instance.filterMapFeatures(this.activeMap.state, filter);
  }

  public notifyBackgroundChange(element: MapInterface) {
    if (element === this.activeMap && !element.background && this.mapInfo.has(element)) {
      this.scheduleReconnect();
    }
  }


  public async applyMapState(
    element: MapInterface,
    options?: { skipDataLayerLoadFilter?: boolean }
  ): Promise<boolean> {

    const { state } = element;

    // First check if the base style has changed
    if (this.previousState && this.previousState.baseStyleId !== state.baseStyleId) {
      const reachable = await this.probeStyleReachable(state.baseStyleId);
      if (!reachable) {
        element.state = { ...state, baseStyleId: this.previousState.baseStyleId };
        this.emitIfMapHostConnected(element, () => element.mapStateUpdated.emit(element.state));
        store.getStore().dispatch(mapUpdateState(element.state));
        return false;
      }
      this.styleUpgradeDegraded = false;
      this.styleChangeRevertId = this.previousState.baseStyleId;
      void this.setMapStyle(state.baseStyleId);
      this.previousState = state;

      // If we are changing the entire style, this process will resume
      // once the style has loaded. So return now
      return false;
    }

    // Handle Scale Bar
    this.updateScaleBar(state.showScaleBar);

    // Handle Project Bounds
    this.updateProjectBounds(state.showAppBounds);

    // Render base layers
    await MapLayerManagerService.instance.synchroniseLayers(state.layers, state.projectId);

    await this.whenIdle();

    // Render data layers
    await MapFeaturesService.instance.applyMapDataLayers(
      state,
      this.previousState,
      element.featureFilter,
      options?.skipDataLayerLoadFilter ?? false
    );

    // Add any custom layers
    for (const layer of element.layers || []) {
      await layer.addTo(this.map);
      layer?.onLayersChanged?.();
    }

    this.previousState = state;
    return true;
  }
}

export default MapService;